import {v4 as uuidv4} from 'uuid';

import makeUseGlobalClientState, {
  UseGlobalClientStateOutput,
} from 'ui-library/hooks/global-client-state/makeUseGlobalClientState';

import type {ChatDraftState} from '../ChatDraftState';
import {setDraftState} from '../ChatDraftState';
import type {SidepanelChat} from './ChatRouter';

const AI_PANEL_STATE_KEY = 'ai-panel-state-v2';

export enum InitialUI {
  SIGNUP = 'signup',
  SETTINGS = 'settings',
}

export interface AIPanelState {
  /**
   * A client-local ID used as the key for the ChatRouter component. This allows us to ensure that
   * state does not leak between chat sessions when rendering ChatRouter. This is simply not the chat
   * ID since that does not exist when a new chat is started.
   */
  clientChatId: string;

  /**
   * The chat session that is currently active.
   */
  activeChat: SidepanelChat;

  // Whether the history is shown.
  showHistory: boolean;

  // The user's preference for whether history is shown.
  showHistoryPreference: boolean;

  initialModal: InitialUI | undefined;

  // Whether the user has clicked "Start" on the onboarding banner, bypassing it to show the full
  // onboarding screen directly. Persisted in global state so navigation away and back doesn't reset it.
  showOnboardingScreen: boolean;
}

export const panelStateHook = makeUseGlobalClientState<AIPanelState>({
  activeChat: {
    id: null,
  },
  showHistory: false,
  showHistoryPreference: false,
  clientChatId: uuidv4(),
  initialModal: undefined,
  showOnboardingScreen: false,
});

export function useAISidepanelGlobalClientState(): UseGlobalClientStateOutput<AIPanelState> {
  return panelStateHook.useGlobalClientState(AI_PANEL_STATE_KEY);
}

export function getAISidepanelState(): AIPanelState {
  return panelStateHook.getState(AI_PANEL_STATE_KEY);
}

export function openExistingChat(chatSessionId: string): void {
  panelStateHook.updateState(AI_PANEL_STATE_KEY, {
    clientChatId: uuidv4(),
    activeChat: {id: chatSessionId, type: null},
    // TODO: Can we avoid resetting these values?
    showHistoryPreference: false,
    showHistory: false,
    initialModal: undefined,
    showOnboardingScreen: false,
  });
}

export function openDraftChat(draftState: ChatDraftState, options: {modal?: InitialUI} = {}): void {
  setDraftState(null, draftState);

  panelStateHook.updateState(AI_PANEL_STATE_KEY, {
    clientChatId: uuidv4(),
    activeChat: {id: null},
    // TODO: Can we avoid resetting these values?
    showHistoryPreference: false,
    showHistory: false,
    initialModal: options.modal,
    showOnboardingScreen: false,
  });
}

export function getActiveChatId(): string | null {
  return getAISidepanelState().activeChat.id;
}
